import "react-medium-image-zoom/dist/styles.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePostGetAllOrders } from "@/api/pizza-checker/orders/orders";
import { OrdersData } from "@/api/custom-schema";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { usePizzaContext } from "@/context/pizza-context";
import { useSelectedOrder } from "@/context/selected-order-context";
import { useBranch } from "@/context/branch-context";
import { PizzaAnnotationPanel } from "@/components/pizzas/pizza-annotation-widget";
import { LayerControlsTable, PizzaEvaluationVisualSimple } from "./components";
import PizzaEvaluationVisual from "./pizza-evaluation-visual";
import { usePizzaNavigation } from "./hooks";
import { isInteractiveTarget } from "./hooks/use-pizza-navigation";
import { useOrderNavigation } from "./hooks/use-order-navigation";
import { mapOrderEventDTOToOrder, type Order } from "@/schemas/order";
import { useSwipe } from "@/hooks/use-swipe";
import { useElementWidth } from "@/hooks/use-element-width";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { formatCaptureCount, formatDailyCounter } from "@/lib/counter-format";
import { orderPizzaItems, parsePizzaName } from "@/lib/order-items";
import { pizzaVerdict } from "@/lib/pizza-verdict";
import { cn } from "@/lib/utils";
import { DetailHeader, MobileNavigationBar, type DetailNavigation } from "@/pizza-detail/detail-header";
import { EvaluationPanel, type ViewerTab } from "@/pizza-detail/evaluation-panel";
import { PizzaFacts } from "@/pizza-detail/pizza-facts";
import { copyToClipboard } from "@/lib/clipboard";
import { shortDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { DeveloperDiagnostics } from "@/pizza-detail/diagnostics/developer-diagnostics";
import { EvaluationVersionsCard } from "@/pizza-detail/evaluation-versions";
import { OrderPizzaStrip } from "@/pizza-detail/order-pizza-strip";

/** what each evaluation layer shows (developer mode), in the current language */
const LAYER_DESCRIPTIONS: Record<string, TranslationKey> = {
  source: "detail.layer.source",
  heatmap: "detail.layer.heatmap",
  heatmap_burnt: "detail.layer.heatmapBurnt",
  heatmap_uncooked: "detail.layer.heatmapUncooked",
  pizza_mask: "detail.layer.pizzaMask",
  crust: "detail.layer.crust",
  pizza_detected_components: "detail.layer.detectedComponents",
};
const QUARTER_LAYERS = ["mask_q1", "mask_q2", "mask_q3", "mask_q4"];

/** Container width from which the photo and the evaluation sit side by side. */
const SIDE_BY_SIDE_MIN = 880;
/** Container width from which the panels under the photo form two columns. */
const TWO_COLUMN_PANELS_MIN = 640;

interface SelectedProductProps {
  /**
   * What the arrows browse: the filtered pizza list (/pizzas), the pizzas of the selected order and then the
   * neighbouring orders (/orders/:orderId/pizza/:pizzaId), or nothing (operator screen).
   */
  navigation?: "pizzas" | "order" | "none";
  /** swipe left/right on a touch screen opens the next/previous pizza */
  swipeNavigation?: boolean;
}

export default function SelectedProduct({
  navigation = "pizzas",
  swipeNavigation = false,
}: SelectedProductProps) {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [isDeveloperMode, setIsDeveloperMode] = useLocalStorageState(
    "pizza-admin:detail-developer-mode:v1",
    false,
  );

  const {
    layers,
    metadata,
    enabledLayers,
    pizza,
    setEnabledLayers,
    layerOpacity,
    setLayerOpacity,
    loading,
    evaluatePizza,
    selectedPizzaId,
    paddedPizzaRoi,
    evaluationData,
    pizzaType,
  } = usePizzaEvaluationContext();
  const { layerBlendMode, setLayerBlendMode } = usePizzaEvaluationContext();
  const {
    pizzas,
    pizzasFetching,
    pizzasUpdatedAt,
    refetchPizzas,
    page,
    setPage,
    perPage,
    totalPages,
    totalPizzas,
    locatePizza,
  } = usePizzaContext();
  const { selectedOrder } = useSelectedOrder();
  const { branches } = useBranch();

  // ---- order of the pizza (daily counter for the header) ----
  const selectedOrderMatchesPizza =
    !!selectedOrder && !!pizza?.order_id && selectedOrder.id === pizza.order_id;
  const { data: pizzaOrderData } = usePostGetAllOrders<OrdersData>(
    {
      skip: 0,
      limit: 1,
      filters: pizza?.order_id ? { "data.id": pizza.order_id } : undefined,
    },
    {
      query: {
        enabled: !!pizza?.order_id && !selectedOrderMatchesPizza,
      },
    },
  );
  const orderDailyCounter = useMemo(() => {
    if (selectedOrderMatchesPizza) {
      return selectedOrder.daily_counter ?? selectedOrder.formatted_id ?? null;
    }

    const orderEvent = pizzaOrderData?.orders?.[0];
    if (!orderEvent) {
      return null;
    }

    const order = mapOrderEventDTOToOrder(orderEvent);
    return order.daily_counter ?? order.formatted_id ?? null;
  }, [
    pizzaOrderData,
    selectedOrder?.daily_counter,
    selectedOrder?.formatted_id,
    selectedOrderMatchesPizza,
  ]);

  // pizza.branch_id is the BiteBerry branch uuid taken from the order; the branch
  // list carries both that and the admin id, so match either.
  const branchName = pizza?.branch_id
    ? (branches?.find(
        (branch) => branch.bb_branch_id === pizza.branch_id || branch.id === pizza.branch_id,
      )?.name ?? null)
    : null;

  // ---- browsing the pizza list ----
  const { goToNextPizza, goToPrevPizza } = usePizzaNavigation({
    pizza,
    pizzas,
    fetching: pizzasFetching,
    updatedAt: pizzasUpdatedAt,
    refetch: refetchPizzas,
    page,
    perPage,
    totalPages,
    setPage,
    locatePizza,
    enabled: navigation === "pizzas",
  });
  const listIndex = pizzas && pizza ? pizzas.findIndex((p) => p.id === pizza.id) : -1;
  // a pizza that is not on the loaded page (opened by a link, or the list moved on): ask for its position
  const [located, setLocated] = useState<{ id: string; position: number | null } | null>(null);
  const needsLocate = navigation === "pizzas" && !!pizza && !!pizzas && listIndex === -1;
  useEffect(() => {
    if (!needsLocate || !pizza) return;
    let cancelled = false;
    locatePizza(pizza)
      .then((position) => {
        if (!cancelled) setLocated({ id: pizza.id, position });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [needsLocate, pizza, locatePizza]);
  const listPosition =
    listIndex !== -1
      ? page * perPage + listIndex
      : located && located.id === pizza?.id
        ? located.position
        : null;

  // ---- browsing the order's pizzas, then the neighbouring orders ----
  const orderItems = useMemo(
    () => (navigation === "order" ? orderPizzaItems(selectedOrder) : []),
    [navigation, selectedOrder],
  );
  const orderIndex = orderItems.findIndex((item) => item.id === selectedPizzaId);
  const orderNav = useOrderNavigation(navigation === "order" ? selectedOrder : null);
  const openOrderPizza = useCallback(
    (order: Order, which: "first" | "last") => {
      const items = orderPizzaItems(order);
      const item = which === "first" ? items[0] : items[items.length - 1];
      navigate(item ? `/orders/${order.id}/pizza/${item.id}` : `/orders/${order.id}`);
    },
    [navigate],
  );
  const orderStep = useCallback(
    (direction: "next" | "prev") => {
      if (!selectedOrder) return;
      const target = orderIndex + (direction === "next" ? 1 : -1);
      if (orderIndex !== -1 && target >= 0 && target < orderItems.length) {
        navigate(`/orders/${selectedOrder.id}/pizza/${orderItems[target].id}`);
        return;
      }
      orderNav.move(direction, (order) =>
        openOrderPizza(order, direction === "next" ? "first" : "last"),
      );
    },
    [selectedOrder, orderIndex, orderItems, navigate, orderNav, openOrderPizza],
  );
  const orderStepRef = useRef(orderStep);
  useEffect(() => {
    orderStepRef.current = orderStep;
  });
  useEffect(() => {
    if (navigation !== "order") return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        isInteractiveTarget(e.target)
      ) {
        return;
      }
      if (e.key === "ArrowRight") orderStepRef.current("next");
      else if (e.key === "ArrowLeft") orderStepRef.current("prev");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigation]);

  const nav: DetailNavigation | undefined =
    navigation === "pizzas"
      ? {
          position: listPosition,
          total: totalPizzas,
          hasPrev: listPosition === null || listPosition > 0,
          hasNext:
            listPosition === null || totalPizzas === undefined || listPosition < totalPizzas - 1,
          onPrev: goToPrevPizza,
          onNext: goToNextPizza,
        }
      : navigation === "order" && selectedOrder
        ? {
            position: orderIndex !== -1 ? orderIndex : null,
            total: orderItems.length,
            unit: t("detail.nav.inOrder"),
            hasPrev: orderIndex > 0 || orderNav.hasPrev,
            hasNext: (orderIndex !== -1 && orderIndex < orderItems.length - 1) || orderNav.hasNext,
            onPrev: () => orderStep("prev"),
            onNext: () => orderStep("next"),
            prevLabel: orderIndex > 0 ? t("common.previous") : t("detail.nav.prevOrder"),
            nextLabel:
              orderIndex !== -1 && orderIndex < orderItems.length - 1
                ? t("common.next")
                : t("detail.nav.nextOrder"),
            busy: orderNav.moving,
          }
        : undefined;

  // ---- layout from the space the detail really gets ----
  const [widthRef, width] = useElementWidth();
  const swipeRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useCallback(
    (node: HTMLDivElement | null) => {
      swipeRef.current = node;
      widthRef(node);
    },
    [widthRef],
  );
  useSwipe(swipeRef, {
    onSwipeLeft: () => nav?.hasNext && nav.onNext(),
    onSwipeRight: () => nav?.hasPrev && nav.onPrev(),
    enabled: swipeNavigation && !!nav,
  });
  const sideBySide = width >= SIDE_BY_SIDE_MIN;
  const twoColumnPanels = !sideBySide && width >= TWO_COLUMN_PANELS_MIN;
  const panelWidth = width >= 1500 ? 420 : width >= 1180 ? 380 : 340;

  // the viewer's active layer: the slicing / obstacle charts make it taller than the screen
  const [viewerTab, setViewerTab] = useState<ViewerTab>("masked");
  const [tabRequest, setTabRequest] = useState<{ tab: ViewerTab; n: number } | null>(null);
  const openTab = useCallback(
    (tab: ViewerTab) => setTabRequest((prev) => ({ tab, n: (prev?.n ?? 0) + 1 })),
    [],
  );
  const stickyViewer =
    sideBySide && !isDeveloperMode && viewerTab !== "slicing" && viewerTab !== "obstacles";

  // ---- developer layers ----
  const toggleLayer = (layerName: string) => {
    setEnabledLayers((prev) => ({
      ...prev,
      [layerName]: !prev[layerName],
    }));
  };
  const changeMode = (layerName: string, mode: string) => {
    setLayerBlendMode((prev) => ({
      ...prev,
      [layerName]: mode,
    }));
  };
  const layerDescription: Record<string, string> = Object.fromEntries([
    ...Object.entries(LAYER_DESCRIPTIONS).map(([name, key]) => [name, t(key)]),
    ...QUARTER_LAYERS.map((name, index) => [name, t("detail.layer.quarter", { n: index + 1 })]),
  ]);
  const maskLayers = layers?.filter((layer) => layer.type === "mask") || [];
  const nonMaskLayers = layers?.filter((layer) => layer.type !== "mask") || [];
  // Only one mask can be enabled at a time
  const handleMaskToggle = (layerName: string) => {
    setEnabledLayers((prev) => {
      const newState: Record<string, boolean> = {};
      maskLayers.forEach((layer) => {
        newState[layer.name] = layer.name === layerName ? !prev[layerName] : false;
      });
      // preserve non-mask layers' state
      Object.keys(prev).forEach((name) => {
        if (!maskLayers.find((l) => l.name === name)) {
          newState[name] = prev[name];
        }
      });
      return newState;
    });
  };
  const handleOpacityChange = (layerName: string, opacity: number) => {
    setLayerOpacity((prev) => ({
      ...prev,
      [layerName]: opacity,
    }));
  };

  // ---- header ----
  const verdict = pizzaVerdict(metadata);
  const { typeId, name } = parsePizzaName(pizza?.name);
  const openOrder = pizza?.order_id
    ? () => {
        navigate(`/orders/${pizza.order_id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    : undefined;
  const captured = pizza?.capture_at ? shortDateTime(pizza.capture_at) : null;
  const meta = [
    branchName,
    pizza?.order_id ? (
      <button
        type="button"
        onClick={openOrder}
        className="font-medium text-foreground/90 underline decoration-dotted underline-offset-2 hover:text-foreground"
      >
        {t("detail.header.order", { number: formatDailyCounter(orderDailyCounter) })}
      </button>
    ) : null,
    captured,
    pizza && (pizza.capture_count ?? 0) > 1
      ? t("detail.header.captureCount", { count: formatCaptureCount(pizza.capture_count) })
      : null,
  ].filter(Boolean);

  const back =
    navigation === "pizzas"
      ? { label: t("detail.header.closeDetail"), onClick: () => navigate("/pizzas") }
      : navigation === "order" && selectedOrder
        ? { label: t("detail.header.backToOrder"), onClick: () => navigate(`/orders/${selectedOrder.id}`) }
        : undefined;

  const showOrderStrip = navigation === "order" && !!selectedOrder && orderItems.length > 1;
  // the photo never gets taller than the screen below the header (and the order strip)
  const imageMaxHeight = `max(${sideBySide ? 20 : 16}rem, 100svh - ${
    (sideBySide ? 11.5 : 13) + (showOrderStrip ? 4 : 0)
  }rem)`;
  const viewer = isDeveloperMode ? (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-xl border bg-black/60">
        <div
          className="mx-auto"
          style={{
            maxWidth: `calc((${imageMaxHeight}) * ${
              paddedPizzaRoi.width / Math.max(paddedPizzaRoi.height, 1)
            })`,
          }}
        >
          <PizzaEvaluationVisual />
        </div>
      </div>
      <section className="rounded-xl border bg-card px-4 py-3">
        <h3 className="mb-3 text-sm font-semibold">{t("detail.layers.title")}</h3>
        <LayerControlsTable
          layers={nonMaskLayers}
          enabledLayers={enabledLayers}
          layerOpacity={layerOpacity}
          layerBlendMode={layerBlendMode}
          layerDescription={layerDescription}
          onToggleLayer={toggleLayer}
          onOpacityChange={handleOpacityChange}
          onBlendModeChange={changeMode}
        />
        <LayerControlsTable
          layers={maskLayers}
          enabledLayers={enabledLayers}
          layerOpacity={layerOpacity}
          layerBlendMode={layerBlendMode}
          layerDescription={layerDescription}
          onToggleLayer={handleMaskToggle}
          onOpacityChange={handleOpacityChange}
          onBlendModeChange={changeMode}
          title={t("detail.layers.maskLayers")}
          isMaskLayer={true}
        />
      </section>
    </div>
  ) : (
    <PizzaEvaluationVisualSimple
      maxImageHeight={imageMaxHeight}
      tabRequest={tabRequest}
      onTabChange={setViewerTab}
    />
  );

  const evaluation = (
    <EvaluationPanel
      metadata={metadata}
      loading={loading}
      isDeveloperMode={isDeveloperMode}
      onOpenTab={isDeveloperMode ? undefined : openTab}
    />
  );
  const facts = (
    <PizzaFacts
      pizza={pizza}
      branchName={branchName}
      orderDailyCounter={orderDailyCounter}
      onOpenOrder={navigation === "order" ? undefined : openOrder}
    />
  );
  const annotation = <PizzaAnnotationPanel />;
  // developer mode: every stored field of the pizza, grouped by pipeline step, below the photo and panel
  const diagnostics = isDeveloperMode && (
    <DeveloperDiagnostics
      pizza={pizza}
      metadata={metadata}
      layers={evaluationData?.layers}
      evaluatedAt={
        evaluationData?.evaluated_at ??
        (pizza?.evaluation as { evaluated_at?: string | null } | null | undefined)?.evaluated_at ??
        null
      }
      evaluationResponse={evaluationData}
      pizzaType={pizzaType}
      branchName={branchName}
    />
  );

  return (
    <div ref={rootRef} className={cn("relative w-full", nav && "pb-20 md:pb-0")}>
      <div className="flex flex-col gap-3">
        <DetailHeader
          title={name || (pizza ? t("common.pizza") : t("common.loading"))}
          typeId={typeId}
          verdict={verdict}
          loading={loading || (!!selectedPizzaId && !pizza)}
          meta={meta}
          back={back}
          nav={nav}
          onReevaluate={() => evaluatePizza(true)}
          reevaluating={loading}
          isDeveloperMode={isDeveloperMode}
          onDeveloperModeChange={setIsDeveloperMode}
          pizzaId={pizza?.id}
          onCopyId={pizza?.id ? () => copyToClipboard(pizza.id, t("detail.pizzaId")) : undefined}
        />

        {showOrderStrip && selectedOrder && (
          <OrderPizzaStrip order={selectedOrder} items={orderItems} currentId={selectedPizzaId} />
        )}

        {sideBySide ? (
          <div
            className="grid items-start gap-4"
            style={{ gridTemplateColumns: `minmax(0, 1fr) ${panelWidth}px` }}
          >
            <div className={cn("min-w-0", stickyViewer && "sticky top-[3.75rem]")}>{viewer}</div>
            <aside className="flex min-w-0 flex-col gap-3">
              {evaluation}
              {annotation}
              {facts}
                        </aside>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {viewer}
            {twoColumnPanels ? (
              <div className="grid grid-cols-2 items-start gap-3">
                <div className="flex min-w-0 flex-col gap-3">{evaluation}</div>
                <div className="flex min-w-0 flex-col gap-3">
                  {annotation}
                  {facts}
                            </div>
              </div>
            ) : (
              <>
                {evaluation}
                {annotation}
                {facts}
                          </>
            )}
          </div>
        )}

        {diagnostics}

        {/* what the evaluation was made with: backend build, settings, models (metadata.versions) */}
        <EvaluationVersionsCard metadata={metadata} />
      </div>

      {nav && <MobileNavigationBar nav={nav} />}
    </div>
  );
}
